import { IVault, Kickback } from '.'
import { Box } from '../../../shared_components/UI/Box'
import styled from 'styled-components'
import { formatDateString } from '../../PoolDetails/Details'
import { obfuscateEthWalletAddress } from '../../../shared_components/SocialConnectButtons/Twitter'
import { amountFromWei } from '../../../utils/amount'
import { XAxis, YAxis, CartesianGrid, Area, AreaChart } from 'recharts'

import { subDays } from 'date-fns'
import { LevelBox, bottomRightOnlyClipPath } from './styles'
import { useWindowSize } from '@uidotdev/usehooks'
import { USDB_DECIMALS } from '../../../config'

const Kickbacks = ({ vault }: { vault: IVault }) => {
  const { kickbacks } = vault
  if (!kickbacks?.length) return <></>
  return <KickbacksTableAndChart kickbacks={kickbacks} />
}

function KickbacksTableAndChart({ kickbacks }: { kickbacks: Kickback[] }) {
  const daysToShow = 7
  // const [daysToShow, setDaysToShow] = useState(7)

  const today = new Date()
  const timeToShowFrom = subDays(today, daysToShow)

  const kickbacksToShow = kickbacks.filter(
    ({ createdAt }) => new Date(createdAt) > timeToShowFrom,
  )

  const totalFromAllKickbacks = kickbacksToShow.reduce(
    (acc, { meta_data }) =>
      acc + amountFromWei(meta_data.amount, USDB_DECIMALS).toNumber(),
    0,
  )

  const kickbacksBundledByDate = {}

  for (let i = 0; i < daysToShow; i++) {
    const date = subDays(today, i).toLocaleDateString()
    if (!kickbacksBundledByDate[date]) {
      kickbacksBundledByDate[date] = []
    }
  }

  kickbacksToShow.forEach((kickback) => {
    const date = new Date(kickback.createdAt).toLocaleDateString()
    if (!kickbacksBundledByDate[date]) kickbacksBundledByDate[date] = []
    kickbacksBundledByDate[date].push(kickback)
  }, {} as Record<string, Kickback[]>)

  const kickbacksFormattedForRecharts = Object.entries(kickbacksBundledByDate)
    .slice()
    .map(([date, kickbacks]) => {
      const amount = (kickbacks as Kickback[]).reduce(
        (acc, { meta_data }) =>
          acc + amountFromWei(meta_data.amount, USDB_DECIMALS).toNumber(),
        0,
      )
      return {
        date,
        amount,
      }
    })
    .slice()
    .reverse()

  const formattedTotal = totalFromAllKickbacks.toFixed(2)

  const formattedTotalIncludesDecimals = formattedTotal.includes('.')

  const formattedTotalString = formattedTotalIncludesDecimals
    ? formattedTotal
    : parseInt(formattedTotal)

  return (
    <Box dc g={30}>
      <GraphContainer dc g={20}>
        <Box ac jsb fu>
          <Box dc g={40}>
            <TitleWithChip title="total earnings" />
            <TotalEarned>${formattedTotalString}</TotalEarned>
          </Box>
          <Box>{/* TODO: date selector here*/}</Box>
        </Box>
        <Box></Box>
        <Box>
          <SimpleLineChart data={kickbacksFormattedForRecharts} />
        </Box>
      </GraphContainer>
      <KickbacksTableContainer dc g={20}>
        <span className="small-cap">Referral Earnings</span>
        <table>
          <thead>
            <tr>
              <th>From</th>
              <th>Amount</th>
              <th>Time</th>
              <th>% Rate</th>
              <th>Tx Hash</th>
            </tr>
          </thead>
          <tbody>
            {kickbacks
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime(),
              )
              .map((kickback, index) => {
                const { id, createdAt, meta_data } = kickback

                const { amount, sender, tx_hash, percentage_of_share } =
                  meta_data

                const isLast = index === kickbacks.length - 1

                const formattedAmount = amountFromWei(
                  amount,
                  USDB_DECIMALS,
                ).toFixed(2)

                const hasDecimals = formattedAmount.includes('.')

                return (
                  <tr key={id} className={isLast ? 'last' : ''}>
                    <td>{obfuscateEthWalletAddress(sender)}</td>
                    <td>
                      $
                      {hasDecimals
                        ? formattedAmount
                        : parseInt(formattedAmount)}
                    </td>
                    <td>{formatDateString(createdAt)}</td>
                    <td>{percentage_of_share}%</td>
                    <td>
                      <a
                        href={
                          process.env.REACT_APP_BLOCK_EXPLORER +
                          `/tx/${tx_hash}`
                        }
                        target="_blank"
                        rel="noopenner noreferrer"
                      >
                        {obfuscateEthWalletAddress(tx_hash)}
                      </a>
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </KickbacksTableContainer>
    </Box>
  )
}

export default Kickbacks

const KickbacksTableContainer = styled(Box)`
  width: 100%;
  padding-top: 4rem;
  padding-bottom: 2rem;

  table {
    width: 100%;
  }
  th {
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px; /* 100% */
    text-transform: uppercase;
    padding-bottom: 1rem;
    text-align: left;
  }
  tr {
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
    letter-spacing: 0.48px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    height: 48px;
  }
  a {
    color: ${({ theme }) => theme.colors.brandPrimary};
  }
  tr.last {
    border-bottom: none;
  }

  overflow-x: auto;

  table {
    min-width: 650px;
  }
`

const GraphContainer = styled(LevelBox)`
  padding-top: 2rem;
  width: 100%;
  margin-top: 4rem;
  padding-bottom: 2rem;
  ${bottomRightOnlyClipPath}
  :after {
    ${bottomRightOnlyClipPath}
  }
  .active-dot {
    display: none;
  }

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`

const SimpleLineChart = ({ data }) => {
  const { width: screenWidth } = useWindowSize()
  const isMobile = screenWidth < 768

  const chartWidth = isMobile ? screenWidth - 63 : screenWidth - 210

  return (
    <AreaChart
      width={chartWidth}
      height={320}
      data={data}
      margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 32,
      }}
    >
      <defs>
        <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#F4ED0C" stopOpacity={0.3} />
          <stop offset="95%" stopColor="#F4ED0C" stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid stroke="rgba(239, 239, 239, 0.08)" vertical={false} />
      <XAxis
        dataKey="date"
        tick={{ fill: '#fff' }}
        tickMargin={32}
        style={{
          fontSize: '12px',
          transform: 'translateX(8px)',
        }}
        axisLine={false}
        tickLine={false}
        tickFormatter={(value) => {
          const jsDate = new Date(value.split('/').reverse())
          const day = jsDate.toLocaleDateString().slice(0, 2)
          const toLocaleDateString = jsDate.toString()
          const shortMonth = toLocaleDateString.split(' ')[1]
          return day + ' ' + shortMonth
        }}
      />
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={{ fill: '#fff' }}
        tickMargin={32}
        style={{
          fontSize: '12px',
        }}
      />
      <Area
        type="monotone"
        dataKey="amount"
        stroke="#F4ED0C"
        fill="url(#colorAmount)"
      />
    </AreaChart>
  )
}

const TotalEarned = styled.span`
  color: #fff;
  font-size: 48px;
  font-style: normal;
  font-weight: 700;
  line-height: 50px; /* 104.167% */
  letter-spacing: -0.48px;
`

export function TitleWithChip({ title }: { title: string }) {
  return (
    <TitleSection ac g={8} fu>
      <div className="chip" />
      <span>{title}</span>
    </TitleSection>
  )
}

const TitleSection = styled(Box)`
  height: auto;
  .chip {
    width: 16px;
    height: 22px;
    background: #f4ed0c;
  }
  span {
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 22px; /* 137.5% */
    letter-spacing: 4.8px;
    text-transform: uppercase;
  }
`
